import { ReactNode } from 'react';

/**
 * convert original chunks to highlighted chunk
 * @param search - search string
 * @param wrapTextLength - wrap text length words before and after search string
 * @param prevChunk - chunk before search string
 * @param nextChunk - chunk after search string
 */
const originalChunksResultFragment = (
  search: string,
  wrapTextLength: number,
  prevChunk: string,
  nextChunk: string,
) => {
  const prevChunkSeparator = prevChunk.endsWith(' ') ? ' ' : '';
  const prevWords = splitTextIntoWords(prevChunk);
  const nextChunkSeparator = nextChunk.startsWith(' ') ? ' ' : '';
  const nextWords = nextChunk.split(' ');
  let prevText = '';
  if (prevWords.length > wrapTextLength) {
    prevText = prevWords
      .splice(prevWords.length - wrapTextLength, prevWords.length)
      .join(' ');
  } else {
    prevText = prevWords.join(' ');
  }
  let nextText = '';
  if (nextWords.length > wrapTextLength) {
    nextText = nextWords.splice(0, wrapTextLength).join(' ');
  } else {
    nextText = nextWords.join(' ');
  }
  return [
    ' ... ',
    <span>
      {prevText}
      {prevChunkSeparator}
      <strong>{search}</strong>
      {nextChunkSeparator}
      {nextText}
    </span>,
  ];
};

function splitTextIntoWords(text: string) {
  // Use a regular expression to match one or more whitespace characters
  const words = text.split(/\s+/);

  // Filter out empty strings that might arise from consecutive spaces
  return words.filter((word) => word !== '');
}

/**
 * create search result highlights array
 * @param search - search string
 * @param resultText - result text from search response
 * @param wrapTextLength - word count before and after search string
 * @param showFullText - show full text with highlights
 */
export const searchResultHighligts = (
  search: string,
  resultText: string,
  wrapTextLength: number = 2,
  showFullText: boolean = false,
) => {
  if (search.length < 3) return resultText.substring(0, 200) + '...';
  if (showFullText) return fulltextHighlight(resultText, search);
  const { chunks, matches } = splitBySearch(resultText, search);
  console.log('higlights: ', chunks, matches);
  if (matches.length <= 0) {
    fulltextHighlight(resultText, search);
  }
  const res: string | ReactNode[] = [];
  for (let i = 0; i < chunks.length - 1; i++) {
    const prevChunk = chunks[i];
    const match = matches[i];
    const nextChunk = chunks[i + 1];
    res.push(
      ...originalChunksResultFragment(
        match,
        wrapTextLength,
        prevChunk,
        nextChunk,
      ),
    );
  }
  return res;
};

export type SearchResultParsed = {
  highlights: ReactNode[];
  matchCount: number;
  fullTextHighlights: string | ReactNode[];
  shortText: string;
};

/**
 * create search result highlights array
 * @param search - search string
 * @param resultText - result text from search response
 * @param wrapTextLength - word count before and after search string
 */
export const searchResultHighligtsObject = (
  search: string,
  resultText: string,
  wrapTextLength: number = 2,
  // showFullText: boolean = false,
): SearchResultParsed => {
  // if (search.length < 3) return resultText.substring(0, 200) + "...";
  // if (showFullText) return fulltextHighlight(resultText, search);
  const fullTextHighlights = fulltextHighlight(resultText, search);
  const { chunks, matches } = splitBySearch(resultText, search);
  console.log('higlights: ', chunks, matches);
  // if (matches.length <= 0) {
  //   fulltextHighlight(resultText, search);
  // }
  const res: string | ReactNode[] = [];
  for (let i = 0; i < chunks.length - 1; i++) {
    const prevChunk = chunks[i];
    const match = matches[i];
    const nextChunk = chunks[i + 1];
    res.push(
      ...originalChunksResultFragment(
        match,
        wrapTextLength,
        prevChunk,
        nextChunk,
      ),
    );
  }
  return {
    highlights: res,
    matchCount: matches.length,
    fullTextHighlights: fullTextHighlights,
    shortText: resultText.substring(0, 200) + '...',
  };
};

/**
 * full text with highlights
 * @param text
 * @param search
 */
export const fulltextHighlight = (text: string, search: string) => {
  if (search.length < 3) return text;
  const { chunks, matches } = splitBySearch(text, search);
  const res: string | ReactNode[] = [];
  for (let i = 0; i < chunks.length - 1; i++) {
    const prevChunk = chunks[i];
    const match = matches[i];
    res.push(prevChunk, <strong>{match}</strong>);
  }
  res.push(chunks[chunks.length - 1]);
  return res;
};

/**
 * split text by search string
 * @param text
 * @param search
 */
function splitBySearch(text: string, search: string) {
  const lowerCaseText = text.toLowerCase();
  const lowerCaseSeparator = search.toLowerCase();

  const chunks = [];
  const matches = [];
  let lastIndex = 0;

  let matchIndex = lowerCaseText.indexOf(lowerCaseSeparator);
  while (matchIndex !== -1) {
    chunks.push(text.substring(lastIndex, matchIndex));
    matches.push(text.substring(matchIndex, matchIndex + search.length));
    lastIndex = matchIndex + search.length;
    matchIndex = lowerCaseText.indexOf(lowerCaseSeparator, lastIndex);
  }
  chunks.push(text.substring(lastIndex));
  return { chunks, matches };
}

// Example usage:
// const text = "This is a TeSt CaSe with Any Text and any text.";
// const separator = "Any Text";
//
// const chunks = splitBySearch(text, separator);
// console.log(chunks);
// Output: ["This is a TeSt CaSe with ", " and anytext."]
